import { useEffect } from 'react';
import { useChatSearchParams } from './useChatSearchParams';
import { useScrollableChatElement } from './useScrollableChatElement';

export const useChatAnchorClickEvent = () => {
  useEffect(() => {
    document.addEventListener('click', function (event) {
      // Check if the clicked element is a link with target="_blank"
      const link = (event.target as HTMLElement).closest('a[target="_blank"]');
      if (!link) {
        return;
      }

      // Make sure it's a left-click without modifier keys (like Ctrl)
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      // Parse href and check origin
      const url = new URL((link as HTMLAnchorElement).href, window.location.href);

      // Prevent browser default
      event.preventDefault();

      // Use window.open so we get an opener
      window.open(url.href, '_blank');
    });
  }, []);
};

export const useChatScrollEvents = () => {
  const { viewMode } = useChatSearchParams();
  const { scrollChatBy, scrollChatToTop } = useScrollableChatElement();

  useEffect(() => {
    if (viewMode) {
      setTimeout(() => {
        scrollChatToTop();
      }, 100);
    }
  }, [viewMode, scrollChatToTop]);

  useEffect(() => {
    let lastTouchY: number | null = null;
    let touchTimeout: NodeJS.Timeout;

    const handleWheel = (event: any) => {
      scrollChatBy(event.deltaY);
      event.preventDefault(); // Prevent default page scrolling
    };

    const handleTouchMove = (event: TouchEvent) => {
      // Check if event is on scrollable chat element
      if ((event.target as HTMLElement).closest('[data-hook="scrollableChatElement"]')) {
        return; // Let event propagate naturally
      }

      const touch = event.touches[0];

      // Only try to prevent default if the event is cancelable
      if (!event.cancelable || !touch) {
        return;
      }

      if (lastTouchY === null) {
        lastTouchY = touch.clientY;
        return;
      }

      const deltaY = lastTouchY - touch.clientY;
      lastTouchY = touch.clientY;

      // Reset lastTouchY after 150ms of no touch events
      clearTimeout(touchTimeout);
      touchTimeout = setTimeout(() => {
        lastTouchY = null;
      }, 150);

      scrollChatBy(deltaY);
      event.preventDefault();
    };

    // Handle both mouse wheel and touch scrolling
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [scrollChatBy]);
};
